<script lang="ts" setup>
import type { InterceptorConfig } from '@upreport/oops-widget';
import { createOopsWidget, LogLevel } from '@upreport/oops-widget';
import { FetchInterceptor } from '@upreport/oops-widget/fetchInterceptor';
import { createAutoDetectI18nProvider } from '@upreport/oops-widget/i18n';
import { XHRInterceptor } from '@upreport/oops-widget/xhrInterceptor';
import { StatusAlert } from '@upreport/oops-widget/statusAlert';

const isDev = import.meta.env.DEV;

const options: InterceptorConfig = {
  showSlowRequestAlerts: true,
  monitoredServicePatterns: ['httpstat.us'],
  monitoredServiceOptions: {
    requireExplicitPatterns: true,
    includeCurrentOrigin: true,
    treatRelativeAsInternal: true,
  },
};

createOopsWidget({
  statusUrl: 'https://oops-widget.up.report/docplanner/websites/status.urlparam',
  alertComponent: StatusAlert,
  placement: 'top-right',
  i18nProvider: createAutoDetectI18nProvider(),
  interceptors: [new FetchInterceptor(options), new XHRInterceptor(options)],
  mobile: {
    enabled: true,
    placement: 'bottom',
  },
  logLevel: LogLevel.DEBUG,
});

// Function to trigger the 500 error request using Fetch
async function trigger500FetchRequest() {
  try {
    const response = await fetch('https://httpstat.us/500');
    const { statusText, status } = await response;
    console.log('Fetch 500 Response status:', {
      statusText,
      status,
    });
  } catch (error) {
    console.error('Fetch 500 Request failed:', error);
  }
}

// Function to trigger the 503 error request using Fetch
async function trigger503FetchRequest() {
  try {
    const response = await fetch('https://httpstat.us/503');
    const { statusText, status } = await response;
    console.log('Fetch 503 Response status:', {
      statusText,
      status,
    });
  } catch (error) {
    console.error('Fetch 503 Request failed:', error);
  }
}

// Function to trigger a long request (40 seconds) using Fetch
async function triggerLongFetchRequest() {
  try {
    console.log('Starting long fetch request...');
    const response = await fetch('https://httpstat.us/200?sleep=40000');
    const { statusText, status } = await response;
    console.log('Long fetch request finished. Response status:', {
      statusText,
      status,
    });
  } catch (error) {
    console.error('Long fetch request failed:', error);
  }
}

// Function to trigger the 500 error request using XHR
function trigger500XHRRequest() {
  const xhr = new XMLHttpRequest();
  xhr.open('GET', 'https://httpstat.us/500');
  xhr.onload = () => {
    console.log('XHR 500 Response status:', {
      statusText: xhr.statusText,
      status: xhr.status,
    });
  };
  xhr.onerror = () => {
    console.error('XHR 500 Request failed');
  };
  xhr.send();
}

// Function to trigger the 503 error request using XHR
function trigger503XHRRequest() {
  const xhr = new XMLHttpRequest();
  xhr.open('GET', 'https://httpstat.us/503');
  xhr.onload = () => {
    console.log('XHR 503 Response status:', {
      statusText: xhr.statusText,
      status: xhr.status,
    });
  };
  xhr.onerror = () => {
    console.error('XHR 503 Request failed');
  };
  xhr.send();
}

// Function to trigger a long request (40 seconds) using XHR
function triggerLongXHRRequest() {
  console.log('Starting long XHR request...');
  const xhr = new XMLHttpRequest();
  xhr.open('GET', 'https://httpstat.us/200?sleep=40000');
  xhr.onload = () => {
    console.log('Long XHR request finished. Response status:', {
      statusText: xhr.statusText,
      status: xhr.status,
    });
  };
  xhr.onerror = () => {
    console.error('Long XHR request failed');
  };
  xhr.send();
}

function triggerMultipleRequests() {
  console.log('Triggering multiple requests (Vue)...');
  // Trigger all fetch requests
  trigger500FetchRequest();
  trigger503FetchRequest();
  triggerLongFetchRequest();

  // Trigger all XHR requests
  trigger500XHRRequest();
  trigger503XHRRequest();
  triggerLongXHRRequest();
}
</script>

<template>
  <div class="min-h-screen flex flex-col items-center pt-8">
    <header class="w-full max-w-5xl px-6 mb-8">
      <div
        class="bg-gradient-to-r from-gray-900 to-gray-800 rounded-lg shadow-lg p-6 flex flex-col items-center"
      >
        <div class="flex items-center mb-4">
          <div class="bg-black bg-opacity-30 px-3 py-1 rounded-md flex items-center mr-2">
            <span class="font-mono font-bold text-2xl">
              <span class="text-syntax-blue">Oops</span>
              <span class="text-syntax-teal">Widget</span>
            </span>
          </div>
          <span class="text-gray-300 font-mono bg-black bg-opacity-20 px-2 py-1 rounded text-sm"
            >Vue Playground</span
          >
        </div>

        <div class="text-xs text-gray-400 font-mono mb-4">
          // Network request testing environment
        </div>

        <nav
          class="w-full max-w-md bg-black bg-opacity-20 rounded-md overflow-hidden border border-gray-700"
        >
          <div class="flex">
            <router-link
              to="/"
              class="flex-1 px-4 py-2 font-mono text-sm text-syntax-blue hover:bg-black hover:bg-opacity-30 transition-colors text-center border-r border-gray-700"
              >Home</router-link
            >
            <router-link
              to="/first"
              class="flex-1 px-4 py-2 font-mono text-sm text-syntax-teal hover:bg-black hover:bg-opacity-30 transition-colors text-center border-r border-gray-700"
              >First</router-link
            >
            <router-link
              to="/second"
              class="flex-1 px-4 py-2 font-mono text-sm text-syntax-purple hover:bg-black hover:bg-opacity-30 transition-colors text-center border-r border-gray-700"
              >Second</router-link
            >
            <a
              href="/vanilla"
              v-if="!isDev"
              class="flex-1 px-4 py-2 font-mono text-sm text-syntax-yellow hover:bg-black hover:bg-opacity-30 transition-colors text-center"
              >Vanilla</a
            >
          </div>
        </nav>
      </div>
    </header>

    <div class="w-full max-w-5xl px-6 mb-8">
      <div class="bg-gradient-to-r from-gray-800 to-gray-900 rounded-lg shadow-lg p-6">
        <h2
          class="font-mono text-lg mb-5 text-center text-gray-300 flex items-center justify-center"
        >
          <span class="bg-black bg-opacity-30 px-3 py-1 rounded-md">
            <span class="text-syntax-purple">request</span>.<span class="text-syntax-teal"
              >controls</span
            ><span class="text-gray-400">();</span>
          </span>
        </h2>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <!-- Fetch Column -->
          <div
            class="flex flex-col items-center gap-3 p-4 bg-black bg-opacity-20 rounded-md border border-gray-700"
          >
            <h3 class="font-mono text-sm text-syntax-blue mb-2 flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4 mr-1"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path
                  d="M20 16V7a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v9m16 0H4m16 0 1.28 2.55a1 1 0 0 1-.9 1.45H3.62a1 1 0 0 1-.9-1.45L4 16"
                ></path>
                <path d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"></path>
              </svg>
              Fetch API
            </h3>
            <button
              @click="trigger500FetchRequest"
              class="btn-hover-effect w-full bg-syntax-blue hover:bg-opacity-90 text-white font-mono py-2 px-4 text-xs rounded transition-all duration-200 border border-black border-opacity-20 shadow-sm flex items-center justify-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-3.5 w-3.5 mr-1.5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              Trigger 500 Fetch
            </button>
            <button
              @click="trigger503FetchRequest"
              class="btn-hover-effect w-full bg-syntax-blue hover:bg-opacity-90 text-white font-mono py-2 px-4 text-xs rounded transition-all duration-200 border border-black border-opacity-20 shadow-sm flex items-center justify-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-3.5 w-3.5 mr-1.5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <line x1="19" y1="5" x2="5" y2="19"></line>
                <circle cx="6.5" cy="6.5" r="2.5"></circle>
                <circle cx="17.5" cy="17.5" r="2.5"></circle>
              </svg>
              Trigger 503 Fetch
            </button>
            <button
              @click="triggerLongFetchRequest"
              class="btn-hover-effect w-full bg-syntax-yellow hover:bg-opacity-90 text-white font-mono py-2 px-4 text-xs rounded transition-all duration-200 border border-black border-opacity-20 shadow-sm flex items-center justify-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-3.5 w-3.5 mr-1.5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              Trigger Long Fetch (40s)
            </button>
          </div>

          <!-- XHR Column -->
          <div
            class="flex flex-col items-center gap-3 p-4 bg-black bg-opacity-20 rounded-md border border-gray-700"
          >
            <h3 class="font-mono text-sm text-syntax-teal mb-2 flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4 mr-1"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <rect x="2" y="2" width="20" height="8" rx="2" ry="2"></rect>
                <rect x="2" y="14" width="20" height="8" rx="2" ry="2"></rect>
                <line x1="6" y1="6" x2="6.01" y2="6"></line>
                <line x1="6" y1="18" x2="6.01" y2="18"></line>
              </svg>
              XHR
            </h3>
            <button
              @click="trigger500XHRRequest"
              class="btn-hover-effect w-full bg-syntax-teal hover:bg-opacity-90 text-white font-mono py-2 px-4 text-xs rounded transition-all duration-200 border border-black border-opacity-20 shadow-sm flex items-center justify-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-3.5 w-3.5 mr-1.5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              Trigger 500 XHR
            </button>
            <button
              @click="trigger503XHRRequest"
              class="btn-hover-effect w-full bg-syntax-teal hover:bg-opacity-90 text-white font-mono py-2 px-4 text-xs rounded transition-all duration-200 border border-black border-opacity-20 shadow-sm flex items-center justify-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-3.5 w-3.5 mr-1.5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <line x1="19" y1="5" x2="5" y2="19"></line>
                <circle cx="6.5" cy="6.5" r="2.5"></circle>
                <circle cx="17.5" cy="17.5" r="2.5"></circle>
              </svg>
              Trigger 503 XHR
            </button>
            <button
              @click="triggerLongXHRRequest"
              class="btn-hover-effect w-full bg-syntax-yellow hover:bg-opacity-90 text-white font-mono py-2 px-4 text-xs rounded transition-all duration-200 border border-black border-opacity-20 shadow-sm flex items-center justify-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-3.5 w-3.5 mr-1.5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              Trigger Long XHR (40s)
            </button>
          </div>

          <!-- Multiple Column -->
          <div
            class="flex flex-col items-center gap-3 p-4 bg-black bg-opacity-20 rounded-md border border-gray-700"
          >
            <h3 class="font-mono text-sm text-syntax-purple mb-2 flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4 mr-1"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path
                  d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m5.66 0H14a2 2 0 0 1 2 2v4.34"
                ></path>
                <polygon points="14 2 20 8 14 14 14 2"></polygon>
              </svg>
              Combined
            </h3>
            <button
              @click="triggerMultipleRequests"
              class="btn-hover-effect w-full bg-syntax-purple hover:bg-opacity-90 text-white font-mono py-2 px-4 text-xs rounded transition-all duration-200 border border-black border-opacity-20 shadow-sm flex items-center justify-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-3.5 w-3.5 mr-1.5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="17 1 21 5 17 9"></polyline>
                <path d="M3 11V9a4 4 0 0 1 4-4h14"></path>
                <polyline points="7 23 3 19 7 15"></polyline>
                <path d="M21 13v2a4 4 0 0 1-4 4H3"></path>
              </svg>
              Trigger Multiple
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="bg-white rounded-md border border-gray-200 shadow-sm p-6 w-full max-w-3xl">
      <router-view />
    </div>
  </div>
</template>

<style>
/* Add button hover effect */
.btn-hover-effect {
  position: relative;
  overflow: hidden;
}
.btn-hover-effect::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  transition: 0.5s;
}
.btn-hover-effect:hover::after {
  left: 100%;
}
</style>
